import React, { useEffect, useRef, useState } from "react";
import { Layout, Menu, Drawer, Grid } from "antd";
import {
  UnorderedListOutlined,
  PlusOutlined,
  UserAddOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  LinkOutlined,
  FileTextOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { PATHS } from "../router/paths";
import TopHeader from "./TopHeader";
import NavTree from "./NavTree";
import { useAuth } from "../../features/auth/model/AuthContext";
import { PullRefreshProvider } from "../../shared/pull-refresh/PullRefreshContext";

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

const menuItems = [
  {
    key: PATHS.ORDERS.ROOT,
    icon: <UnorderedListOutlined />,
    label: "注文一覧",
    title_header: "注文一覧",
  },
  {
    key: "orders",
    icon: <PlusOutlined />,
    label: "新規注文",
    title_header: "新規注文",
    children: [
      { key: PATHS.ORDERS.CREATE_SINGLE, label: "単品注文" },
      { key: PATHS.ORDERS.CREATE_MULTI, label: "複数商品注文" },
    ],
  },
  {
    key: PATHS.MASTER_DATA.CUSTOMERS,
    icon: <UserAddOutlined />,
    label: "顧客管理",
    title_header: "顧客管理",
  },
  {
    key: PATHS.WAIT_TIME.ROOT,
    icon: <ClockCircleOutlined />,
    label: "待機時間管理",
    title_header: "待機時間管理",
  },
  {
    key: PATHS.SALES.ROOT,
    icon: <UserOutlined />,
    label: "営業担当者管理",
    title_header: "営業担当者管理",
  },
  // {
  //   key: PATHS.SELLER_PRODUCTS.ROOT,
  //   icon: <LinkOutlined />,
  //   label: "販売担当商品設定",
  //   title_header: "販売担当商品設定",
  // },
  // {
  //   key: PATHS.COMPANY_REGISTRATIONS.ROOT,
  //   icon: <AuditOutlined />,
  //   label: "会社登録申請管理",
  //   title_header: "会社登録申請管理",
  // },
  // {
  //   key: PATHS.PROPOSES.ROOT,
  //   icon: <FileTextOutlined />,
  //   label: "販売提案管理",
  //   title_header: "販売提案管理",
  // },
  {
    key: PATHS.PRODUCT_DEADLINE.ROOT,
    icon: <CalendarOutlined />,
    label: "注文期限管理",
    title_header: "注文期限管理",
  },
];

export const MainLayout: React.FC = () => {
  const { user } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    let frame = 0;
    const originalOverflow = content.style.overflowY;
    const syncPopupScrollLock = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const hasOpenPicker = Array.from(
          document.querySelectorAll<HTMLElement>(
            ".ant-select-dropdown, .ant-picker-dropdown",
          ),
        ).some((popup) => {
          const style = window.getComputedStyle(popup);
          return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            popup.getClientRects().length > 0
          );
        });

        content.style.overflowY = hasOpenPicker ? "hidden" : originalOverflow;
      });
    };

    const observer = new MutationObserver(syncPopupScrollLock);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "aria-hidden"],
    });
    window.addEventListener("resize", syncPopupScrollLock);
    syncPopupScrollLock();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncPopupScrollLock);
      cancelAnimationFrame(frame);
      content.style.overflowY = originalOverflow;
    };
  }, []);
  const visibleMenuItems = menuItems.filter((item) => {
    const isProposal = item.key === PATHS.PROPOSES.ROOT;
    const canAccessSalePages =
      user?.roles.includes("SALE") ||
      user?.roles.includes("PROPOSAL_COORDINATOR") ||
      user?.roles.includes("ADMIN");
    if (isProposal) {
      return (
        user?.permissions.includes("propose.coordinator.manage") ||
        user?.permissions.includes("propose.seller.contribute")
      );
    }
    if (item.key === PATHS.SALES.ROOT) {
      return user?.permissions.includes("sales.manage") ?? false;
    }
    return canAccessSalePages;
  });

  const onMenuClick = (key: string) => {
    navigate(key);
    setDrawerVisible(false);
  };

  const onShowSidebar = () => {
    if (screens.md) {
      setCollapsed(!collapsed);
    } else {
      setDrawerVisible(true);
    }
  };

  return (
    <Layout style={{ minHeight: "100vh", overflow: "hideen" }}>
      {/* SIDEBAR DESKTOP */}
      {screens.md && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
          width={240}
          theme="light"
          collapsedWidth={0}
          style={{
            overflow: "hidden",
            position: "absolute",
            height: "100vh",
            zIndex: 1000,
            boxShadow: collapsed ? "none" : "2px 0 8px rgba(0,0,0,0.15)",
          }}
        >
          <div
            style={{
              height: 64,
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              padding: "0 24px",
              fontSize: 18,
              fontWeight: "bold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            らくらく受注
          </div>
          <Menu
            theme="light"
            mode="inline"
            selectedKeys={[location.pathname]}
            items={visibleMenuItems.map((parent) => ({
              ...parent,
              onClick: !parent.children
                ? () => onMenuClick(parent.key)
                : undefined,
              children: parent.children?.map((child) => ({
                ...child,
                onClick: () => onMenuClick(child.key),
              })),
            }))}
          />
        </Sider>
      )}

      <Layout
        style={{
          marginLeft: screens.md && !collapsed ? 240 : 0,
          transition: "margin-left 0.2s",
          height: "100vh",
        }}
      >
        {/* HEADER */}
        <TopHeader title={<NavTree />} onShowSidebar={onShowSidebar} />

        {/* CONTENT */}
        <Content
          ref={contentRef}
          style={{
            flex: 1,
            overflowY: "auto",
            padding: 18,
            borderRadius: 8,
          }}
        >
          <PullRefreshProvider scrollContainerRef={contentRef}>
            <Outlet />
          </PullRefreshProvider>
        </Content>
      </Layout>

      {/* DRAWER MOBILE */}
      <Drawer
        title="らくらく受注"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={visibleMenuItems.map((parent) => ({
            ...parent,
            onClick: !parent.children
              ? () => onMenuClick(parent.key)
              : undefined,
            children: parent.children?.map((child) => ({
              ...child,
              onClick: () => onMenuClick(child.key),
            })),
          }))}
        />
      </Drawer>
    </Layout>
  );
};
